//
// # HTML Canvas Root
//

import typia from "typia";
// Local Imports
import { logger, Result, validate as v } from "CoreUtils";
import { schema } from "GenAlphaSchema";
import type { Editor, RedrawRequest } from "../../editor";
import type {
  DrawTo,
  LayoutDisplayOptions,
  SchematicDisplayOptions,
} from "DrawCore/src/draw";
import { Canvas as ICanvas } from "EditorCore/src/canvas";
import { DrawToHtmlCanvas } from "./drawto";
import { WorldTransform, WorldTransforms } from "DrawCore/src/transforms";
import { SpriteCache } from "./sprite";
import { ViewPort } from "./viewport";
import { RenderStats } from "DrawCore/src/stats";

export interface HtmlCanvasOptions {
  width?: number;
  height?: number;
}
export const HtmlCanvasOptions = {
  validator: new v.Validator(typia.createValidate<HtmlCanvasOptions>()),
};

export class HtmlCanvas implements ICanvas {
  //
  readonly worldTransforms: WorldTransforms = WorldTransforms.default(); // World transforms between schematic and page coordinates.
  private canvasParentDiv: HTMLDivElement = document.createElement("div"); // Parent `div` element to the canvas
  private canvas: HTMLCanvasElement; // The HTML Canvas element
  private ctx: CanvasRenderingContext2D; // The canvas rendering context
  public drawTo: DrawToHtmlCanvas | null = null;
  readonly stats = new RenderStats(); // Performance counters and stats
  readonly spriteCache = new SpriteCache(); // Cache for sprites
  // Rendering context set by LayoutContent before each render call.
  // These are read by DrawToHtmlCanvas to provide pdk and layer visibility
  // to entity redraw methods without walking the entity parent chain.
  pdk: schema.pdk.PDK | null = null;
  hiddenLayers: ReadonlySet<number> = new Set();
  // Layout display options — shared reference set once at content-load time
  // via Editor.syncDisplayOptions(); mutated in-place by toggle methods.
  layoutDisplay: LayoutDisplayOptions | null = null;
  // Schematic label visibility — shared reference set once at content-load time
  // via Editor.syncDisplayOptions(); mutated in-place by toggle methods.
  schematicDisplay: SchematicDisplayOptions | null = null;
  private dpr: number = 1; // Device pixel ratio for high-DPI rendering
  private cssWidth: number = 0; // CSS width in logical pixels
  private cssHeight: number = 0; // CSS height in logical pixels
  private pendingBufferWidth: number = 0; // Deferred canvas buffer width (physical pixels)
  private pendingBufferHeight: number = 0; // Deferred canvas buffer height (physical pixels)
  private resizePending: boolean = false; // Whether a buffer resize is waiting for the next render
  //
  constructor(
    readonly editor: Editor, // Reference to the parent Editor
    readonly options?: HtmlCanvasOptions
  ) {
    this.canvas = document.createElement("canvas");

    // Get device pixel ratio for high-DPI/Retina display support
    this.dpr = window.devicePixelRatio || 1;

    // Calculate CSS dimensions (logical pixels)
    this.cssWidth = options?.width ?? window.innerWidth;
    this.cssHeight = options?.height ?? window.innerHeight;

    // Set canvas buffer size to account for device pixel ratio
    this.canvas.width = Math.floor(this.cssWidth * this.dpr);
    this.canvas.height = Math.floor(this.cssHeight * this.dpr);

    // Set the canvas element style to sit "behind" the overlay contents
    const { style } = this.canvas;
    style.position = "absolute";
    style.zIndex = "1";
    // Set CSS size to logical pixels so the canvas displays at the correct size
    style.width = `${this.cssWidth}px`;
    style.height = `${this.cssHeight}px`;

    // Set the parent div size to match the window
    this.canvasParentDiv.style.width = `${this.cssWidth}px`;
    this.canvasParentDiv.style.height = `${this.cssHeight}px`;
    this.canvasParentDiv.appendChild(this.canvas);

    // Add the canvas to our parent
    const { parentDomElement } = this.editor;
    parentDomElement.appendChild(this.canvasParentDiv);

    // Get the rendering context
    const ctx = this.canvas.getContext("2d");
    if (!ctx) {
      throw new Error("Failed to get 2D rendering context from canvas");
    }
    this.ctx = ctx;
  }

  static fromOptions(editor: Editor, options: unknown): HtmlCanvas {
    //
    const validated = HtmlCanvasOptions.validator.validate(options);
    if (validated.ok) {
      // If the options are valid, create a new canvas.
      return new HtmlCanvas(editor, validated.val);
    } else {
      // If the options are invalid, just nag and use the default options.
      logger.warn(`Invalid HtmlCanvas options: ${validated.err}`);
      return new HtmlCanvas(editor, undefined);
    }
  }

  // The size of the canvas, in CSS pixels (logical pixels).
  // This returns the logical size, not the physical pixel buffer size.
  get size() {
    return { width: this.cssWidth, height: this.cssHeight };
  }

  // The device pixel ratio for high-DPI rendering.
  get devicePixelRatio(): number {
    return this.dpr;
  }

  // Main rendering API
  render = (fn: (to: DrawTo) => unknown): unknown => {
    // Apply any pending resize right before clearing + redrawing.
    // Both the buffer dimensions (canvas.width/height) and CSS dimensions
    // (canvas.style) are deferred to here so the old content is never
    // stretched or cleared until the new frame is ready to draw.
    if (this.resizePending) {
      this.canvas.width = this.pendingBufferWidth;
      this.canvas.height = this.pendingBufferHeight;
      this.canvas.style.width = `${this.cssWidth}px`;
      this.canvas.style.height = `${this.cssHeight}px`;
      this.resizePending = false;
    }
    this.clear(); // Clear the canvas
    this.applyWorldTransforms(); // Re-apply the world transforms
    this.drawTo = new DrawToHtmlCanvas(this, this.ctx, this.viewport()); // Create a new DrawTo instance
    const r = fn(this.drawTo);
    this.drawTo = null; // Clear the drawTo after rendering
    return r;
  };

  viewport = (): ViewPort => {
    // Transform the page coordinates to schematic coordinates
    const { width, height } = this.size;
    const pageBbox = {
      top: 0,
      bottom: height,
      left: 0,
      right: width,
    };
    const bbox = this.worldTransforms.toSchBbox(pageBbox);
    return ViewPort.finite(bbox);
  };

  // Set the size of the canvas to match the window.
  sizeToWindow = (): RedrawRequest =>
    this.resizeTo(window.innerWidth, window.innerHeight);

  // Set the size of the canvas to match a container element's client dimensions.
  // Use this instead of sizeToWindow() when the canvas lives inside a panel,
  // so it fills the panel rather than the whole browser window.
  sizeToElement = (element: HTMLElement): RedrawRequest =>
    this.resizeTo(element.clientWidth, element.clientHeight);

  // Shared resize implementation. Updates CSS dimensions and the canvas buffer
  // size (accounting for device pixel ratio), then schedules a redraw.
  private resizeTo = (cssWidth: number, cssHeight: number): RedrawRequest => {
    // Update device pixel ratio (may change if window moves between displays)
    this.dpr = window.devicePixelRatio || 1;

    // Read the element's layout size
    this.cssWidth = cssWidth;
    this.cssHeight = cssHeight;

    // Defer canvas resize (both buffer and CSS) to render() to prevent flicker.
    // Updating CSS immediately would stretch the old buffer content to the new
    // size; deferring ensures the canvas stays at its old size until the new
    // frame is drawn. The parent div resizes immediately for correct layout flow.
    this.pendingBufferWidth = Math.floor(this.cssWidth * this.dpr);
    this.pendingBufferHeight = Math.floor(this.cssHeight * this.dpr);
    this.resizePending = true;

    // Parent div resizes immediately so the layout system flows correctly
    this.canvasParentDiv.style.width = `${this.cssWidth}px`;
    this.canvasParentDiv.style.height = `${this.cssHeight}px`;
    return this.editor.requestRedraw();
  };

  // Clear everything from the canvas, and recreate our drawing context.
  clear = () => {
    this.ctx.setTransform(1, 0, 0, 1, 0, 0); // Reset to identity transform for clearing
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height); // Clear the entire canvas in untransformed coordinates
    this.stats.reset(); // Reset performance stats
  };

  // Apply our transforms to the canvas context.
  // Generally needs to happen whenever either `transforms` or `stage` changes.
  applyWorldTransforms = () => {
    const { scale, offset } = this.worldTransforms.schToPage;
    this.ctx.setTransform(1, 0, 0, 1, 0, 0); // Reset transform
    this.ctx.scale(this.dpr, this.dpr); // Scale for high-DPI displays
    this.ctx.translate(offset.x, offset.y); // Apply translation
    this.ctx.scale(scale, -scale); // Apply scale with y-axis inversion
  };

  // Update our transforms-pair from either a schematic-to-page or page-to-schematic transform.
  updateWorldTransforms = (
    xform: WorldTransform,
    kind: "schToPage" | "pageToSch"
  ): RedrawRequest => {
    this.worldTransforms.update(xform, kind);
    return this.editor.requestRedraw();
  };

  svgString = (): Result<string> => {
    return Result.Err({ why: "no-svg-from-canvas" });
  };

  // Capture the canvas as a base64-encoded PNG data URL
  toDataURL = (type: string = "image/png"): Result<string> => {
    try {
      const dataUrl = this.canvas.toDataURL(type);
      // Strip the "data:image/png;base64," prefix to return just the base64 data
      const base64Data = dataUrl.replace(/^data:image\/\w+;base64,/, "");
      return Result.Ok(base64Data);
    } catch (e) {
      return Result.Err({ why: "canvas-capture-failed", error: String(e) });
    }
  };

  // Boolean indication of whether the canvas is currently rendering.
  get rendering(): boolean {
    return this.drawTo !== null;
  }

  // Invalidate any caching, e.g. due to a global change.
  invalidate(): void {
    this.spriteCache.clear(); // Clear the sprite cache
    this.stats.reset();
  }
}
